import { useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useMultiRoster } from '@/context/MultiRosterContext';
import { ROSTER_FORMATS } from '@/types/rosterFormats';
import { ConfirmationModal } from './ConfirmationModal';

const actionButtonClass =
  'inline-flex min-h-10 items-center justify-center rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-white/30 hover:bg-white/10';

export const RosterSelector: React.FC<{ extraActions?: ReactNode }> = ({ extraActions }) => {
  const {
    rosters,
    activeRosterId,
    activeRoster,
    setActiveRoster,
    createRoster,
    deleteRoster,
    updateRosterName,
    isLoading
  } = useMultiRoster();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [editingRosterId, setEditingRosterId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newRosterName, setNewRosterName] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('standard');
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; rosterId: string | null; rosterName: string | null }>({
    isOpen: false,
    rosterId: null,
    rosterName: null
  });

  const handleCreateRoster = async () => {
    if (!newRosterName.trim()) return;

    try {
      const rosterId = await createRoster(newRosterName.trim(), selectedFormat);
      await setActiveRoster(rosterId);
      setShowCreateForm(false);
      setNewRosterName('');
      setSelectedFormat('standard');
      toast.success(`${newRosterName.trim()} is ready`);
    } catch (error) {
      console.error('Failed to create roster:', error);
      toast.error('Could not create that roster.');
    }
  };

  const handleConfirmDelete = async () => {
    if (confirmDelete.rosterId) {
      try {
        await deleteRoster(confirmDelete.rosterId);
        toast.success('Roster deleted');
      } catch (error) {
        console.error('Failed to delete roster:', error);
        toast.error('Could not delete that roster.');
      }
    }
    setConfirmDelete({ isOpen: false, rosterId: null, rosterName: null });
  };

  const handleRenameRoster = async (rosterId: string) => {
    if (!editingName.trim()) return;
    try {
      await updateRosterName(rosterId, editingName.trim());
      setEditingRosterId(null);
      setEditingName('');
    } catch (error) {
      console.error('Failed to rename roster:', error);
    }
  };

  return (
    <div className="relative">
      <div>
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="min-w-0 w-full text-left"
        >
          <p className="truncate text-base font-medium text-white">
            {activeRoster?.name || (rosters.length === 0 ? 'No roster yet' : 'Pick a roster')}
          </p>
          <p className="mt-0.5 text-xs text-zinc-300">
            {activeRoster
              ? `${activeRoster.format.name} · ${activeRoster.players.length} players`
              : rosters.length === 0
                ? 'Name a team to get started'
                : 'Select a roster'}
          </p>
        </button>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowCreateForm(true)}
            className={actionButtonClass}
          >
            New roster
          </button>
          {extraActions}
        </div>
      </div>

      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute left-0 right-0 z-20 mt-2 max-h-80 overflow-y-auto rounded-2xl border border-white/10 bg-ink shadow-xl"
          >
            {rosters.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-zinc-500">
                No rosters yet. Use New roster to start.
              </p>
            ) : (
              rosters.map((roster) => (
                <div
                  key={roster.id}
                  className={`flex items-center gap-3 border-b border-white/5 px-4 py-3 last:border-b-0 ${
                    roster.id === activeRosterId ? 'bg-white/5' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={async () => {
                      await setActiveRoster(roster.id);
                      setShowDropdown(false);
                    }}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="text-sm font-medium text-white">
                      {roster.name}
                      {roster.id === activeRosterId ? (
                        <span className="ml-2 text-xs text-fiesta-yellow">Active</span>
                      ) : null}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {roster.format.name} · {roster.players.length} players
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingRosterId(roster.id);
                      setEditingName(roster.name);
                    }}
                    className="text-xs text-zinc-500 hover:text-white"
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete({
                      isOpen: true,
                      rosterId: roster.id,
                      rosterName: roster.name
                    })}
                    className="text-xs text-zinc-500 hover:text-rose-300"
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowCreateForm(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-md rounded-2xl border border-white/10 bg-ink p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-display text-lg font-semibold text-white">New roster</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <label className="mb-2 block text-sm text-zinc-400">Roster name</label>
                  <input
                    type="text"
                    value={newRosterName}
                    onChange={(e) => setNewRosterName(e.target.value)}
                    placeholder="My team"
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-zinc-500"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm text-zinc-400">League format</label>
                  <select
                    value={selectedFormat}
                    onChange={(e) => setSelectedFormat(e.target.value)}
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white"
                  >
                    {Object.values(ROSTER_FORMATS).map((format) => (
                      <option key={format.id} value={format.id} className="bg-ink">
                        {format.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-zinc-500">
                    {ROSTER_FORMATS[selectedFormat]?.description}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateRoster}
                  disabled={!newRosterName.trim() || isLoading}
                  className="flex-1 rounded-full bg-fiesta-yellow px-4 py-2 text-sm font-medium text-ink disabled:opacity-50"
                >
                  {isLoading ? 'Creating' : 'Create'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editingRosterId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setEditingRosterId(null)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-sm rounded-2xl border border-white/10 bg-ink p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="mb-4 font-display text-lg font-semibold text-white">Rename roster</h3>
              <input
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                className="mb-4 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setEditingRosterId(null)}
                  className="flex-1 rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleRenameRoster(editingRosterId)}
                  disabled={!editingName.trim() || isLoading}
                  className="flex-1 rounded-full bg-fiesta-yellow px-4 py-2 text-sm font-medium text-ink disabled:opacity-50"
                >
                  {isLoading ? 'Saving' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showDropdown ? (
        <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
      ) : null}

      <ConfirmationModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, rosterId: null, rosterName: null })}
        onConfirm={handleConfirmDelete}
        title="Delete roster"
        message={`Delete ${confirmDelete.rosterName || 'this roster'}? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="red"
      />
    </div>
  );
};
